<template>
  <v-card>
    <v-card-title class="pb-0">
      <v-layout>
        <h1 class="headline">
          Редактирование Продавца
        </h1>
        <v-spacer />
        <v-chip small>
          <span>Автор:&nbsp;</span>
          <span class="font-weight-bold">{{
            author ? author : "не указан"
          }}</span>
        </v-chip>
      </v-layout>
    </v-card-title>
    <v-card-text class="pt-2">
      <v-form v-model="isValid">
        <v-layout column>
          <v-flex xs12>
            <v-layout row>
              <v-flex xs6>
                <TitleTextField
                  v-model="title"
                  :required="true"
                  :rules="[rules.required, rules.min]"
                  label="Наименование*"
                  class="mx-2 mb-2"
                  @input="isChanged = true"
                />
              </v-flex>
              <v-flex xs6>
                <MultipleReferenceCompact
                  v-model="contactPersons"
                  class="mx-2 mb-2"
                  resource="contact-persons"
                  :required="true"
                  :rules="[rules.empty]"
                  component-name="ContactPerson"
                  label="Контактное лицо*"
                  :outline="true"
                  :height-field="44"
                  @input="isChanged = true"
                />
              </v-flex>
            </v-layout>

            <v-layout row>
              <v-flex xs6>
                <EditableList
                  v-model="phones"
                  class="mx-2 mb-4"
                  label="Телефон"
                  :mask="getMask('phone')"
                  :hint="hint"
                  filter="phoneInternational"
                  outline
                  :height="'auto'"
                  title="Редактирование номеров телефонов"
                  data-test="phone"
                  @change="changePhones"
                />
              </v-flex>
              <v-flex xs6>
                <EditableList
                  v-model="emails"
                  class="mx-2 mb-4"
                  label="Email"
                  :rules="[rules.email]"
                  outline
                  :height="'auto'"
                  title="Редактирование e-mail"
                  data-test="email"
                  @change="changeEmails"
                />
              </v-flex>
            </v-layout>

            <v-layout row>
              <v-flex xs12>
                <v-text-field
                  v-model="notes"
                  class="mx-2"
                  label="Специфика"
                  outline
                  height="44"
                  @change="isChanged = true"
                />
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>

        <v-layout
          row
          class="mt-3"
        >
          <v-flex
            xs8
            class="mr-3"
          >
            <v-layout column>
              <v-flex xs12>
                <LoadingPlaces
                  v-model="loadingPlaces"
                  :factory-id="id"
                  :items="loadingPlaces"
                  @create="isChanged = true"
                  @change="isChanged = true"
                  @delete="isChanged = true"
                  @cancel="cancelChangeLoadingPlace"
                />
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex xs4>
            <v-card min-height="100%">
              <v-card-title>
                <h2 class="subheading font-weight-bold">
                  Документы
                  <span
                    v-if="attachments.length > 0"
                    class="blue--text text--darken-4"
                  >({{ attachments.length }})</span>
                </h2>
              </v-card-title>
              <Attachments
                v-model="attachments"
                :resource="`factories/${id}`"
                :disabled-upload-to-server="isNew"
                :disabled-upload="isNew"
                :disabled-sender="isNew"
                :disabled-download="isNew"
              />
            </v-card>
          </v-flex>
        </v-layout>

        <v-layout class="mt-3">
          <v-btn
            small
            color="primary"
            :disabled="!(isValid && isChanged) || isRemoved || loading"
            data-test="saveButton"
            @click="submit(false)"
          >
            Сохранить
          </v-btn>
          <v-btn
            small
            color="primary"
            :disabled="!(isValid && isChanged) || isRemoved || loading"
            data-test="saveAndCloseButton"
            @click="saveAndClose"
          >
            Сохранить и закрыть
          </v-btn>
          <v-btn
            small
            data-test="cancelButton"
            @click="cancel"
          >
            Отмена
          </v-btn>
          <v-spacer />
          <v-btn
            v-if="id !== '0'"
            small
            :color="isRemoved ? 'success' : 'error'"
            :data-test="isRemoved ? 'restoreButton' : 'deleteButton'"
            @click="changeRemovedState"
          >
            {{ isRemoved ? "Восстановить" : "Удалить" }}
          </v-btn>
        </v-layout>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import { NestedEditorsEventBus } from "../../components/nested-editors/nested-editors-event-bus.js";
import ConfirmMixin from "../../mixins/confirm-cancel-changes.js";
import Attachments from "../../components/attachments/Attachments.vue";
import LoadingPlaces from "./LoadingPlaces.vue";
import * as factoriesApi from "../../backend/factoriesApi.js";

export default {
  components: {
    Attachments,
    LoadingPlaces,
  },
  mixins: [ConfirmMixin],
  props: ["id"],
  data() {
    return {
      isValid: false,
      isChanged: false,
      rules: {
        empty: v => !!v || "",
        required: v => !!v || "Поле обязательно к заполнению",
        min: v =>
          v.length >= 3 || "Поле должно содержать не менее 3-х символов",
        email: v => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(v) || v === "" || "Не корректно введен e-mail.";
        },
      },
      resource: "factories",
      addMessage: "Продавец добавлен",
      editMessage: "Продавец изменен",
      title: "",
      phones: [],
      emails: [],
      notes: "",
      contactPersons: [],
      loadingPlaces: [],
      attachments: [],
      isRemoved: false,
      hint: "Номер телефона уже добавлен",
      loading: false,
      author: null,
    };
  },
  computed: {
    isModal() {
      let parent = this.$parent;
      while (parent) {
        if (parent.$options._componentTag === "NestedEditors") {
          return true;
        }
        parent = parent.$parent;
      }
      return false;
    },
    isNew() {
      return +this.id ? false : true;
    },
  },
  created() {
    if (+this.id) {
      this.getData();
    }
  },
  mounted() {
    if (this.isModal) {
      NestedEditorsEventBus.$emit("modal-editor-mounted");
    }
  },
  methods: {
    changePhones(val) {
      this.isChanged = true;
      this.phones = val;
    },
    changeEmails(val) {
      this.isChanged = true;
      this.emails = val;
    },
    getMask(controlType) {
      switch (controlType) {
        case "phone":
          return "+##(###)###-##-##";
        default:
          return "";
      }
    },
    async getData() {
      const res = await factoriesApi.getFactory(this.resource, this.id);
      this.title = res.data.Title;
      this.phones = res.data.Phones;
      this.emails = res.data.Emails;
      this.notes = res.data.Notes;
      this.isRemoved = res.data.IsRemoved;
      this.contactPersons =
        res.data.ContactPersons && res.data.ContactPersons.length != 0
          ? res.data.ContactPersons.map(cp => {
            return {
              id: cp.Id,
              title: cp.Title,
            };
          })
          : [];
      this.loadingPlaces = res.data.LoadingPlaces.map(lp => {
        return {
          id: lp.Id,
          address: lp.Address
            ? { id: lp.Address.Id, title: lp.Address.Title }
            : null,
          timetable: lp.Timetable,
          contactPersons: lp.ContactPersons.map(cp => {
            return {
              id: cp.Id,
              title: cp.FullName,
            };
          }),
        };
      });
      this.author = res.data.Author?.Title || "";
      if (res.data.Attachments) {
        this.attachments = res.data.Attachments.map(a => {
          return {
            id: a.Id,
            title: a.Title,
          };
        });
      }
    },
    async submit(closeAfterSubmit) {
      const loadingPlaces = [];
      this.loadingPlaces.forEach(lp => {
        loadingPlaces.push({
          id: lp.id,
          addressId: lp.address.id,
          timetable: lp.timetable,
          contactPersonsIds: lp.contactPersons
            ? lp.contactPersons.map(cp => cp.id)
            : [],
        });
      });

      const factory = {
        title: this.title,
        phones: this.phones,
        emails: this.emails,
        notes: this.notes,
        contactPersonsIds: this.contactPersons.map(i => i.id),
        factoryLoadingPlaces: loadingPlaces,
      };

      if (this.id && this.id > 0) {
        this.loading = true;
        await factoriesApi.updateFactory(this.resource, this.id, factory);
        this.showInfo(this.editMessage);
        this.isChanged = false;
        this.loading = false;
        if (closeAfterSubmit) {
          this.$router.go(-1);
        }
        this.getData();
      } else {
        this.loading = true;
        const res = await factoriesApi.createFactory(this.resource, factory);
        this.saveAttachments(res.data, closeAfterSubmit);
        this.showInfo(this.addMessage);
        this.isChanged = false;
        this.loading = false;
      }
    },
    cancel() {
      if (this.isModal) {
        NestedEditorsEventBus.$emit("close");
      } else {
        this.$router.go(-1);
      }
    },
    async saveAndClose() {
      await this.submit(true);
    },
    async changeRemovedState() {
      await factoriesApi.changeRemovedState(
        this.resource,
        this.id,
        this.isRemoved,
      );
      this.isRemoved = !this.isRemoved;
    },
    cancelChangeLoadingPlace() {
      this.isChanged = true;
    },
    async saveAttachments(id, closeAfterSubmit) {
      await factoriesApi.saveFactoryAttachments(
        this.resource,
        id,
        this.attachments,
      );
      if (this.isModal) {
        NestedEditorsEventBus.$emit("create", id);
      } else {
        this.$router.replace(`/${this.resource}/${id}`);
        if (closeAfterSubmit) {
          this.$router.go(-1);
        }
      }
    },
    resetLoaders() {
      this.loading = false;
    },
  },
};
</script>
